import type { CodexLoginResult, ProviderDto } from '@af/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Button, Spinner } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Code, Ext } from '@/components/ui/guide';
import { ErrorBox } from '@/components/ui/misc';
import { call } from '@/lib/api';
import { qk } from '@/lib/queries';

export function CodexConnection({
  provider,
  onClose,
  autoStart = false,
}: {
  provider: ProviderDto;
  onClose: () => void;
  /** Start the sign-in right away (used straight after adding the provider). */
  autoStart?: boolean;
}) {
  const qc = useQueryClient();
  const [login, setLogin] = useState<CodexLoginResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (login?.status !== 'pending') return;
    let active = true;
    const timer = setInterval(() => {
      void call('pollCodexLogin', { params: { id: provider.id, loginId: login.loginId }, body: {} })
        .then((r) => {
          if (!active) return;
          setLogin(r);
          if (r.status === 'connected') void qc.invalidateQueries({ queryKey: qk.providers });
        })
        .catch(() => {
          if (active) {
            setError('Login expired. Try connecting again.');
            setLogin(null);
          }
        });
    }, 2500);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, [login?.loginId, login?.status, provider.id, qc]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: start once, on mount only
  useEffect(() => {
    if (autoStart) void start();
  }, []);
  const start = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await call('startCodexLogin', { params: { id: provider.id }, body: {} });
      setLogin(result);
      if (result.status === 'connected') await qc.invalidateQueries({ queryKey: qk.providers });
    } catch {
      setError('Could not start ChatGPT login. Check that the CLI runner is available.');
    } finally {
      setBusy(false);
    }
  };
  const disconnect = async () => {
    setBusy(true);
    try {
      await call('disconnectCodex', { params: { id: provider.id }, body: {} });
      await qc.invalidateQueries({ queryKey: qk.providers });
      onClose();
    } catch {
      setError('Could not disconnect. Try again.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open
      onOpenChange={(v) => {
        if (!v) onClose();
      }}
    >
      <DialogContent title="Connect Codex with ChatGPT">
        <div className="flex flex-col gap-4">
          <p className="text-sm text-muted">
            Codex agents use your ChatGPT plan. Your plan's usage limits apply. Other runtimes and background
            services need an API key.
          </p>
          {error ? <ErrorBox error={error} /> : null}
          {login?.status === 'failed' ? <ErrorBox error={login.error} /> : null}
          {login?.status === 'pending' ? (
            <div className="flex flex-col gap-2">
              {login.verificationUrl ? (
                <>
                  <Ext href={login.verificationUrl}>Open ChatGPT login</Ext>
                  <p>
                    Enter this code: <Code>{login.userCode}</Code>
                  </p>
                </>
              ) : (
                <Spinner />
              )}
              <p className="text-xs text-muted">
                Enable device-code login in your ChatGPT security settings. Waiting for sign-in…
              </p>
            </div>
          ) : login?.status === 'connected' ? (
            <p>
              Connected{login.email ? ` as ${login.email}` : ''}
              {login.planType ? ` (${login.planType})` : ''}. Codex now uses the subscription.
            </p>
          ) : (
            <Button loading={busy} onClick={() => void start()}>
              {provider.subscriptionTokenHint ? 'Reconnect ChatGPT' : 'Connect ChatGPT'}
            </Button>
          )}
          {provider.subscriptionTokenHint || login?.status === 'pending' || login?.status === 'connected' ? (
            <Button variant="ghost" loading={busy} onClick={() => void disconnect()}>
              {login?.status === 'pending' ? 'Cancel sign-in and disconnect' : 'Disconnect ChatGPT'}
            </Button>
          ) : null}
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
